/**
 * The personal working set (docs/2026-09-21-queue-and-cards.md). VIP, then customer ask,
 * then due nearby (seven local days), then what the item is asking for
 * (docs/2026-09-25-attention-ranking.md). Cadences are a sibling list and do not count against
 * the cap. Same rank on Teams and the web: one function, and a surface that has not loaded a
 * thread simply has nothing to raise. The Teams digest further drops daily clocks and anything
 * farther than nearby (docs/2026-09-22-digest-cadences.md).
 */
import { v0 } from "@trellis/model";
import type { AttentionSignal } from "./attention.js";
import { addCalendarDays } from "./dates.js";

export const NEARBY_DAYS = 7;
export const TEAMS_NON_CADENCE = 3;
/** Teams digest only. Two clocks; one action per line (22 Sep, phone still slow after ShowCards came off). */
export const TEAMS_CADENCE = 2;
export const WEB_NON_CADENCE = 20;

export type QueueItem = {
  kind?: string;
  cadenceId?: string | null;
  vipRelation?: string | null;
  /**
   * What the item is asking for, and who is on the thread it came from (attention.ts). Absent on a
   * surface that has not loaded the thread, and absent means nothing raises it, so the rank below is
   * the same function everywhere and answers the same way wherever the data has not arrived yet.
   */
  attention?: { score: number; signals: AttentionSignal[] } | undefined;
  dueDate: string;
  dueDateStated?: boolean;
  createdAt: string;
};

/** A VIP asker names one on the commitment; a customer or VIP on the thread is the same fact. */
function raised(c: Pick<QueueItem, "vipRelation" | "attention">): boolean {
  return !!c.vipRelation || !!c.attention?.signals.includes("vip-on-thread");
}

export function isCadenceItem(c: { kind?: string; cadenceId?: string | null }): boolean {
  return c.kind === "cadence" || !!c.cadenceId;
}

/** 0 overdue, 1 today, 2 nearby, 3 later, 4 undated. */
export function dueBucket(c: Pick<QueueItem, "dueDate" | "dueDateStated">, today: string): number {
  if (c.dueDateStated === false) return 4;
  if (c.dueDate < today) return 0;
  if (c.dueDate === today) return 1;
  if (c.dueDate <= addCalendarDays(today, NEARBY_DAYS)) return 2;
  return 3;
}

export function compareQueue(a: QueueItem, b: QueueItem, today: string): number {
  const vip = (raised(a) ? 0 : 1) - (raised(b) ? 0 : 1);
  if (vip) return vip;
  const customer = (a.kind === "ask" ? 0 : 1) - (b.kind === "ask" ? 0 : 1);
  if (customer) return customer;
  const bucket = dueBucket(a, today) - dueBucket(b, today);
  if (bucket) return bucket;
  // Inside one bucket, what the message is asking for. It refines the date rather than jumping it:
  // work the person has already accepted and is late on stays ahead of anything unconfirmed.
  const attention = (b.attention?.score ?? 0) - (a.attention?.score ?? 0);
  if (attention) return attention;
  const byDue = a.dueDate.localeCompare(b.dueDate);
  if (byDue) return byDue;
  return b.createdAt.localeCompare(a.createdAt);
}

export function splitCadences<T extends { kind?: string; cadenceId?: string | null }>(items: T[]): { work: T[]; cadences: T[] } {
  const work: T[] = [];
  const cadences: T[] = [];
  for (const i of items) (isCadenceItem(i) ? cadences : work).push(i);
  return { work, cadences };
}

export function takeRanked<T extends QueueItem>(items: T[], cap: number, today: string): { shown: T[]; held: number } {
  const shown = [...items].sort((a, b) => compareQueue(a, b, today)).slice(0, cap);
  return { shown, held: Math.max(0, items.length - shown.length) };
}

export function cadenceFrequencyKind(cadenceId: string | undefined | null): string | undefined {
  if (!cadenceId) return undefined;
  return v0.cadences.find((c) => c.id === cadenceId)?.frequency.kind;
}

/**
 * Daily clocks are calendar invites. Weekly, monthly, period, contract-event
 * and other non-daily clocks belong on the digest when they are overdue, due
 * today, or due within seven days.
 */
export function cadenceOnDigest(c: Pick<QueueItem, "dueDate" | "dueDateStated" | "cadenceId">, today: string): boolean {
  if (cadenceFrequencyKind(c.cadenceId) === "daily") return false;
  return dueBucket(c, today) <= 2;
}

function cadenceDef(cadenceId: string | undefined | null) {
  if (!cadenceId) return undefined;
  return v0.cadences.find((c) => c.id === cadenceId);
}

/**
 * Reaches the customer: a produced artifact is audience `customer`, the
 * evidence is a send-event, the role is customer-contact, or the clock is a
 * contract / period event. Same produce-and-audience test as the model
 * (`packages/model/src/model.test.ts`). Monthly contract meeting is the
 * standing customer meeting and has no produced artifact.
 */
export function cadenceIsCustomerFacing(cadenceId: string | undefined | null): boolean {
  const c = cadenceDef(cadenceId);
  if (!c) return false;
  if ((c.produces ?? []).some((id) => v0.artifacts.find((a) => a.id === id)?.audience === "customer")) return true;
  if (c.evidence === "send-event") return true;
  if (c.runsBy === "customer-contact") return true;
  if (c.frequency.kind === "per-contract-event" || c.frequency.kind === "per-period-of-performance") return true;
  return c.id === "monthly-contract-meeting";
}

/** Rarer clocks first. There is no quarterly kind; period and on-trigger cover that grain. */
export function cadenceRarity(cadenceId: string | undefined | null): number {
  switch (cadenceFrequencyKind(cadenceId)) {
    case "per-period-of-performance": return 0;
    case "per-contract-event": return 1;
    case "per-item-clock": return 2;
    case "on-trigger": return 3;
    case "monthly": return 4;
    case "weekly": return 5;
    case "daily": return 6;
    default: return 5;
  }
}

/** Customer-facing, then immediacy, then rarer clocks, then how loud a miss is. */
export function compareCadence(a: QueueItem, b: QueueItem, today: string): number {
  const facing = (cadenceIsCustomerFacing(a.cadenceId) ? 0 : 1) - (cadenceIsCustomerFacing(b.cadenceId) ? 0 : 1);
  if (facing) return facing;
  const bucket = dueBucket(a, today) - dueBucket(b, today);
  if (bucket) return bucket;
  const rare = cadenceRarity(a.cadenceId) - cadenceRarity(b.cadenceId);
  if (rare) return rare;
  const loud = (cadenceDef(a.cadenceId)?.missedIs === "escalation" ? 0 : 1) - (cadenceDef(b.cadenceId)?.missedIs === "escalation" ? 0 : 1);
  if (loud) return loud;
  const byDue = a.dueDate.localeCompare(b.dueDate);
  if (byDue) return byDue;
  return b.createdAt.localeCompare(a.createdAt);
}

/** Filter, then customer-facing rank. Held is eligible leftovers, not dropped dailies. */
export function takeDigestCadences<T extends QueueItem>(items: T[], cap: number, today: string): { shown: T[]; held: number } {
  const eligible = items.filter((i) => cadenceOnDigest(i, today));
  const shown = [...eligible].sort((a, b) => compareCadence(a, b, today)).slice(0, cap);
  return { shown, held: Math.max(0, eligible.length - shown.length) };
}
